import React, { useState } from 'react';
import { Button, Form, Input, InputNumber, Modal, Select, Space, Switch, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { epcService } from '@/services/epc.service';

const TYPES = [
  'Weather',
  'Material',
  'Permit',
  'Labour',
  'Equipment',
  'Design',
  'Client',
  'Vendor',
  'Access',
  'Other',
];
const STATUSES = ['Open', 'Under_Review', 'EoT_Candidate', 'Accepted', 'Rejected', 'Closed'];
const SEVERITIES = ['Low', 'Medium', 'High', 'Critical'];

type Props = { projectId?: string };

export const DelaysPanel: React.FC<Props> = ({ projectId }) => {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [form] = Form.useForm();

  const packagesQ = useQuery({
    queryKey: ['cep-packages', projectId],
    queryFn: () => epcService.getExecutionPackages(projectId),
    enabled: !!projectId,
    staleTime: 30_000,
  });

  const delaysQ = useQuery({
    queryKey: ['cep-delays', projectId],
    queryFn: () => epcService.getCepDelays(projectId),
    enabled: !!projectId,
    staleTime: 15_000,
  });

  const createMut = useMutation({
    mutationFn: (values: Record<string, unknown>) =>
      epcService.createDelay({ ...values, projectId }),
    onSuccess: () => {
      message.success('Delay registered (EOIP advisory if elevated / EoT)');
      setOpen(false);
      form.resetFields();
      void qc.invalidateQueries({ queryKey: ['cep-delays'] });
      void qc.invalidateQueries({ queryKey: ['cep-work-fronts'] });
      void qc.invalidateQueries({ queryKey: ['cep-dashboards'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  const patchMut = useMutation({
    mutationFn: ({ id, status }: { id: string; status: string }) =>
      epcService.patchDelay(id, { status }),
    onSuccess: () => {
      message.success('Delay updated');
      void qc.invalidateQueries({ queryKey: ['cep-delays'] });
      void qc.invalidateQueries({ queryKey: ['cep-work-fronts'] });
      void qc.invalidateQueries({ queryKey: ['cep-dashboards'] });
    },
    onError: (e: Error) => message.error(e.message),
  });

  if (!projectId) return <Tag>Select a project for the delay register</Tag>;

  const rows = (delaysQ.data as any[]) || [];
  const packages = (packagesQ.data as any[]) || [];

  return (
    <div>
      <Space style={{ marginBottom: 12 }} wrap>
        <Button type="primary" onClick={() => setOpen(true)}>
          Log delay
        </Button>
        <Tag color="geekblue">CEP Wave 5</Tag>
        <Tag>EoT candidates are advisory — no contract amendment</Tag>
      </Space>
      <Table
        size="small"
        rowKey={(r) => String(r.id)}
        loading={delaysQ.isLoading}
        dataSource={rows}
        columns={[
          { title: 'Type', dataIndex: 'delay_type', width: 120 },
          { title: 'Title', dataIndex: 'title', ellipsis: true },
          {
            title: 'Status',
            dataIndex: 'status',
            width: 150,
            render: (v: string, r: any) => (
              <Select
                size="small"
                style={{ width: 140 }}
                value={v}
                options={STATUSES.map((s) => ({ value: s, label: s }))}
                onChange={(status) => patchMut.mutate({ id: String(r.id), status })}
              />
            ),
          },
          { title: 'Severity', dataIndex: 'severity', width: 100 },
          {
            title: 'EoT',
            dataIndex: 'eot_candidate',
            width: 80,
            render: (v: number) => (v ? <Tag color="orange">Yes</Tag> : <Tag>—</Tag>),
          },
          { title: 'Hours', dataIndex: 'duration_hours', width: 80 },
        ]}
      />
      <Modal
        title="Log delay"
        open={open}
        onCancel={() => setOpen(false)}
        onOk={() => form.submit()}
        confirmLoading={createMut.isPending}
        destroyOnClose
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={(v) => createMut.mutate(v)}
          initialValues={{ status: 'Open', severity: 'Medium', eotCandidate: false }}
        >
          <Form.Item name="delayType" label="Type" rules={[{ required: true }]}>
            <Select options={TYPES.map((t) => ({ value: t, label: t }))} />
          </Form.Item>
          <Form.Item name="title" label="Title" rules={[{ required: true }]}>
            <Input />
          </Form.Item>
          <Form.Item name="packageId" label="Execution package">
            <Select
              allowClear
              options={packages.map((p: any) => ({
                value: String(p.id),
                label: p.package_number,
              }))}
            />
          </Form.Item>
          <Form.Item name="severity" label="Severity">
            <Select options={SEVERITIES.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item name="status" label="Status">
            <Select options={STATUSES.map((s) => ({ value: s, label: s }))} />
          </Form.Item>
          <Form.Item name="durationHours" label="Duration (hours)">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="eotDays" label="EoT days claimed">
            <InputNumber min={0} style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="eotCandidate" label="EoT candidate" valuePropName="checked">
            <Switch />
          </Form.Item>
          <Form.Item name="responsibleParty" label="Responsible party">
            <Input />
          </Form.Item>
          <Form.Item name="description" label="Description">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default DelaysPanel;
